import { Injectable } from '@angular/core';
import { SearchService } from '../../../../shared/search/search.service';
import { SearchParams } from '../../../../shared/search/models/search-params.model';
import { SearchStatus } from '../../../../shared/search/models/search-status.model';
import { PagedData } from '../../../../shared/search/models/paged-data.model';
import { TableRow } from '../../../../shared/search/models/table-row.model';
import { PageDefinition } from '../../../../shared/search/models/page-definition.model';
import { Observable } from 'rxjs/Observable';
import { CoreServiceRegistry } from '../../../../core/core-service-registry';
import { SharedServiceRegistry } from '../../../../shared/shared-service-registry';
import { SearchEvent } from '../../../../shared/search/models/search-event';
import { InventoryReplenishDataService } from './inventory-replenish-data.service';
import { NEW_SEARCH_EVENT } from '../../../../shared/search/search-constants';
import { LoaderEnabled } from '../../../../shared/components/loader/loader.service';



@Injectable()
export class InventoryReplenishSearchService extends SearchService {

    private dataService: InventoryReplenishDataService;


    constructor(private coreServiceRegistry: CoreServiceRegistry,
        private sharedServiceRegistry: SharedServiceRegistry,
        public searchParams: SearchParams,
        public searchStatus: SearchStatus) {
        super(coreServiceRegistry.http$, searchParams, searchStatus,
            coreServiceRegistry.resourceService,
            sharedServiceRegistry.userPreferencesService, sharedServiceRegistry.alertService,
            sharedServiceRegistry.dateConfigService);
        this.pagedData = new PagedData<TableRow>();
    }

    public setDataService(dataService: InventoryReplenishDataService) {
        this.dataService = dataService;
    }

    public performSearch() {
        this.fetchSearchResults()
            .subscribe((data: any) => {
                this.searchStatus.hasResults = true;
                this.searchStatus.recordCount = this.pagedData.pageDefinition.recordCount;
                const event = new SearchEvent(NEW_SEARCH_EVENT);
                event.isSearchSuccess = true;
                this.publishEvent(event);
            });
    }

    @LoaderEnabled()
    public fetchSearchResults(): Observable<Response> {

        this.pagedData.pageRows = [];
        const tableId = this.searchParams.tableId;
        const pageInfoReq = {
            tableId: tableId,
            pageNo: this.pagedData.pageDefinition.pageNumber,
            sortColumn: this.pagedData.pageDefinition.sortColumn,
            sortOrder: this.pagedData.pageDefinition.sortOrder,
            pageSize: this.pagedData.pageDefinition.pageSize,
            fullFetch: this.searchParams.isFullFetch,
            fetchAllrecords: this.searchParams.paginateLocally
        };
            const payload = {
                pageInfo: pageInfoReq,
                searchCriteria:
                this.formatSearchCriteria(this.searchCriteria, this.searchParams.additionalParamMap, this.filterCondition)
            };

        const dataUrl$: any
            = this.http$.post(`api/inventory/replenish/lotloc/dropdowns`, JSON.stringify(payload));
        return dataUrl$
            .map((response: any) => {
                    this.transformSearchResponse(response);
                    return response;
            })
        .catch((errorObject: any) => {
          if (errorObject.error && errorObject.error.errors && errorObject.error.errors.length !== 0) {
            if (errorObject.status === 400) {
              this.alertService.clearAll().error(errorObject.error.errors['0'].defaultMessage + ' - ' +
                errorObject.error.errors['0'].rejectedValue);
            // LFWM-2934-handling 500 internalserver error.
            } else if (errorObject.status === 500) {
              this.alertService.clearAll().error(this.resourceService.get(errorObject.error.errors['0'].validationMessage));
            }
          } else if (errorObject.error && errorObject.error.errors && errorObject.error.errors[0]) {
            this.alertService.error(errorObject.error.errors[0].validationMessage);
          }
          return Observable.throw('Failed to fetch  Search Results :: ' + errorObject.message);
        });
    }

    public formatSearchCriteria(searchCriteria, additionalParamMap, filterCondition?: any) {
        if (!searchCriteria) {
          return {};
        }
        let allConditions = null;
        // Code added to merge filter conditions with master search criteria, if filter condition is there.
        if (filterCondition && filterCondition.clauses.length > 0) {
          allConditions = JSON.parse(JSON.stringify(searchCriteria.conditions));
          if (allConditions && allConditions.length > 0) {
            allConditions[allConditions.length - 1].operator = 'AND';
          }
          allConditions.push(filterCondition);
        } else {
          allConditions = searchCriteria.conditions;
        }
        const conditionReqList = [];
        allConditions.forEach((condition: any, index: any) => {
          const clauseReqlist = [];
          condition.clauses.forEach((clause: any) => {
            if (clause.value !== undefined && clause.value !== null) {
              clauseReqlist.push({
                column: clause.column,
                operation: clause.operation,
                value: clause.value,
                value1: clause.value1,
                logicalOperation: clause.logicalOperation
              });
            }
          });
          if (clauseReqlist.length !== 0) {
            let logOp = null;
            if (index < allConditions.length - 1) {
              logOp = condition.operator ? condition.operator : condition.logicalOperation ;
            }
            conditionReqList.push({
              mandatory: condition.isMandatory,
              clauses: clauseReqlist,
              logicalOperation: logOp
            });
          }
        });
        if (additionalParamMap) {
          const additionalClauses = [];
          const additionalParams = additionalParamMap;
          const additionalProps = Object.keys(additionalParams);
          if (additionalProps.length > 0) {
            const lastCondition = conditionReqList.slice(conditionReqList.length - 1)[0];
            lastCondition.logicalOperation = 'AND';
            additionalProps.forEach((property: string, index: number) => {
              additionalClauses.push({
                column: property,
                operation: '=',
                value: additionalParams[property],
                logicalOperation: index === 0 ? '' : 'AND'
              });
            });
            conditionReqList.push({
              mandatory: false,
              clauses: additionalClauses,
              logicalOperation: ''
            });
          }
        }
        return { conditions: conditionReqList };
      }

    public transformSearchResponse(response: any) {
        const pDef: PageDefinition = this.dataService.getDetailsSearchService().pagedData.pageDefinition;
        const pData: Array<TableRow> = this.dataService.getDetailsSearchService().pagedData.pageRows;
        pDef.pageNumber = +response['replenishResponse']['pageNo'];
        pDef.sortColumn = response['replenishResponse']['sortColumn'];
        pDef.sortOrder = +response['replenishResponse']['sortOrder'];
        pDef.recordCount = +response['replenishResponse']['totalRecords'];
        pDef.pageSize = +response['replenishResponse']['pageSize'];
        for (let i = 0; i < response['replenishResponse']['pageData'].rows.length; i++) {
            // Adding code for DataTable View Structure Transformation
            const rowDefinition = response['replenishResponse']['pageData'].rows[i];
            const rowObject: any = new Object();
            rowObject.rIdx = rowDefinition.rIdx;
            // Flag to select/deselect the respective Checkbox (default : unchecked)
            rowObject.isSelected = false;
            // Iterating and inserting the server response cells to each Row Object in RowData Array
            rowObject.cells = {};
            rowDefinition.cells.forEach((cell: any) => {
                const cellInfo: any = new Object();
                cellInfo.cVal = cell.cVal;
                cellInfo.cValOrig = cell.cVal;
                cellInfo.cValPrev = cell.cVal;
                cellInfo.cName = cell.cName;
                rowObject.cells[cell.cName] = cellInfo;
            });
            pData.push(rowObject);
        }
        const lotLocMap = response.lotlocMap;

        const replenKeysFrmLotLocMap = Object.keys(lotLocMap);
        replenKeysFrmLotLocMap.forEach(replenKey => {

            const locMap = lotLocMap[replenKey].locMap;
            const locMapReplnKeys = Object.keys(locMap);
            locMapReplnKeys.forEach(locMapReplnKey => {
                locMap[locMapReplnKey].forEach((keyValuPair) => {
                    keyValuPair.value = keyValuPair.key;
                });
            });

            const lotList = lotLocMap[replenKey].lotList;
            lotList.forEach((lotKeyValuePair) => {
                lotKeyValuePair.value = lotKeyValuePair.key;
            });

            const movIdMap = lotLocMap[replenKey].movIdMap;
            const movIdReplnKeys = Object.keys(movIdMap);
            movIdReplnKeys.forEach(moveIdReplenKey => {
                const movIddrpdownObject = movIdMap[moveIdReplenKey];
                const moveIdReplenKeydrpdwnArray = Object.keys(movIddrpdownObject);
                moveIdReplenKeydrpdwnArray.forEach(moveIdReplenKeydrpdwnelement => {
                    movIddrpdownObject[moveIdReplenKeydrpdwnelement].forEach((drpdwnelements) => {
                        drpdwnelements.value = drpdwnelements.key;
                    });
                });
            });

        });
        this.dataService.lotLineMap = response.lotlocMap;
        // response.lotlocMap.forEach(element => {
        //     this.dataService.lotLineMap[Object.keys(element)[0]] = Object.values(element)[0];
        // });
        // this.dataService.lotLineMap.push(response.lotlocMap);
    }

}



// WEBPACK FOOTER //
// /home/runner/work/fbm-fulfillment-wms-client/fbm-fulfillment-wms-client/src/app/features/inventory/inventory-replenish/services/inventory-replenish-search.service.ts